import type { ControllerMode } from "@/data/types";
import { MODE_CATALOG } from "@/data/modeCatalog";
import { NumberField } from "@/components/ui/NumberField";
import { LOCALE } from "@/lib/format";
import { useT } from "@/lib/i18n";

/**
 * Per-mode parameter form. Reads field metadata from the single MODE_CATALOG
 * (key, label, unit, signed flag, default, min). The form renders nothing
 * for modes with no parameters (gridZeroControl, OFF). On change we emit
 * the FULL params object so the parent (entry, default, or override) can
 * persist it verbatim.
 *
 * Units (CLAR-10): watt-denominated params are DISPLAYED AND ENTERED in kW
 * — the unit every read-only surface of the app uses — with a live
 * "= 80,000 W" echo under the field so magnitude errors are visible before
 * save. The wire payload stays watt-denominated (edge contract locked):
 * param keys keep their `_W` names, values convert ×1000 on the way out
 * and ÷1000 on the way in. Only this form layer converts.
 *
 * Validation: the edge re-validates everything, but we clamp non-signed
 * fields to a minimum of 0 (in kW) on the way out so a trivial typo
 * doesn't make the SPA disagree with the edge's accepted range.
 */
export function ParamsEditor({
  mode,
  value,
  onChange,
  size = "md",
}: {
  mode: ControllerMode;
  value: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
  /** "sm" shrinks inputs for the OverridePanel; "md" used in the editor. */
  size?: "sm" | "md";
}) {
  const t = useT();
  const params = MODE_CATALOG[mode].params;
  if (params.length === 0) {
    return (
      <div className="text-[11px] text-text-faint">
        {mode === "OFF" ? t("manage.params.offNone") : t("manage.params.none")}
      </div>
    );
  }
  const inputSize = size === "sm" ? "h-8 text-xs" : "h-9 text-sm";
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {params.map((p) => {
        const current = value[p.key] ?? p.default;
        // Watt params present as kW in the form (CLAR-10); everything
        // else (currently nothing) passes through untouched.
        const isWatts = p.unit === "W";
        const displayValue = isWatts ? current / 1000 : current;
        const displayUnit = isWatts ? "kW" : p.unit;
        const min = p.signed ? undefined : (p.min ?? 0) / (isWatts ? 1000 : 1);
        return (
          <label key={p.key} className="block">
            <div className="mb-1 text-[11px] font-medium text-text">
              {p.label}{" "}
              <span className="font-normal text-text-faint">({displayUnit})</span>
            </div>
            <NumberField
              value={displayValue}
              onChange={(v) =>
                onChange({ ...value, [p.key]: isWatts ? Math.round(v * 1000) : v })
              }
              min={min}
              allowSigned={p.signed ?? false}
              className={`w-full ${inputSize}`}
            />
            {isWatts && (
              <div className="num mt-1 text-[10px] text-text-muted">
                {t("manage.params.wattsEcho", { watts: Math.round(current).toLocaleString(LOCALE) })}
              </div>
            )}
            <div className="mt-1 text-[10px] text-text-faint">{p.hint}</div>
          </label>
        );
      })}
    </div>
  );
}
